import { useState } from 'react';
import { Bot, ChevronRight, Clock, RefreshCw, AlertTriangle, CheckCircle2, XCircle } from 'lucide-react';
import { useAgentTasks, type AgentTask } from '../lib/data';
import { SkeletonTable } from './Skeleton';

const STATUS_TONES: Record<string, { bg: string; fg: string }> = {
  pending: { bg: 'rgba(100,116,139,0.15)', fg: '#94a3b8' },
  running: { bg: 'rgba(59,130,246,0.15)', fg: '#3b82f6' },
  succeeded: { bg: 'rgba(34,197,94,0.15)', fg: '#22c55e' },
  failed: { bg: 'rgba(239,68,68,0.15)', fg: '#ef4444' },
  cancelled: { bg: 'rgba(148,163,184,0.15)', fg: '#94a3b8' },
};

function StatusBadge({ status }: { status: string }) {
  const tone = STATUS_TONES[status] ?? { bg: 'rgba(100,116,139,0.15)', fg: '#94a3b8' };
  return (
    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wide" style={{ background: tone.bg, color: tone.fg }}>
      {status}
    </span>
  );
}

function fmtDur(ms: number): string {
  if (ms <= 0) return '—';
  const s = Math.floor(ms / 1000);
  const m = Math.floor(s / 60);
  const h = Math.floor(m / 60);
  return h > 0 ? `${h}h${m % 60}m` : m > 0 ? `${m}m${s % 60}s` : `${s}s`;
}

export function AgentTasks() {
  const [filter, setFilter] = useState<{ status?: string; source?: string }>({});
  const { tasks, loading, error, retry } = useAgentTasks(filter);
  const [selected, setSelected] = useState<AgentTask | null>(null);

  if (loading) return <SkeletonTable rows={6} />;
  if (error) return (
    <div className="flex flex-col gap-3 p-6">
      <p className="text-sm text-red-500">{error}</p>
      <button onClick={retry} className="text-xs text-accent underline">Retry</button>
    </div>
  );

  const list = tasks ?? [];

  return (
    <div className="p-6">
      <div className="flex flex-wrap items-start justify-between gap-3 mb-4">
        <div>
          <h2 className="text-lg font-semibold">Agent OS — Executed Tasks</h2>
          <p className="text-xs text-muted-foreground">Tasks decomposed by the agent OS and dispatched to the runtime engine (Temporal + sandbox). Traces + logs link via trace_id.</p>
        </div>
        <div className="flex flex-wrap items-center gap-2 min-w-0 max-w-full">
          <select
            value={filter.status ?? ''}
            onChange={(e) => setFilter((f) => ({ ...f, status: e.target.value || undefined }))}
            className="text-xs px-2 py-1.5 rounded-md border focus:outline-none min-w-0 max-w-full"
          >
            <option value="">All statuses</option>
            <option value="pending">pending</option>
            <option value="running">running</option>
            <option value="succeeded">succeeded</option>
            <option value="failed">failed</option>
            <option value="cancelled">cancelled</option>
          </select>
          <select
            value={filter.source ?? ''}
            onChange={(e) => setFilter((f) => ({ ...f, source: e.target.value || undefined }))}
            className="text-xs px-2 py-1.5 rounded-md border focus:outline-none min-w-0 max-w-full"
          >
            <option value="">All sources</option>
            <option value="issue">issue</option>
            <option value="pr">pr</option>
            <option value="manual">manual</option>
          </select>
          <button onClick={retry} className="flex items-center gap-1.5 text-xs px-2.5 py-1.5 rounded-md border hover:bg-accent/5">
            <RefreshCw className="w-3.5 h-3.5" /> Refresh
          </button>
        </div>
      </div>

      <div className="flex flex-col gap-1.5">
        {list.map((t) => (
          <button
            key={t.id}
            onClick={() => setSelected(t)}
            className={`text-left px-3 py-2.5 rounded-md border text-sm transition-colors ${selected?.id === t.id ? 'border-accent bg-accent/5' : 'hover:bg-accent/5'}`}
          >
            <div className="flex items-start gap-2 min-w-0">
              <Bot className="w-3.5 h-3.5 text-accent shrink-0 mt-0.5" />
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 min-w-0">
                  <span className="font-medium truncate">{t.title || t.sourceId}</span>
                  <StatusBadge status={t.status} />
                </div>
                <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5 text-[10px] text-muted-foreground">
                  <span className="truncate max-w-full">{t.source} · {t.repoFull}</span>
                  <span className="inline-flex items-center gap-0.5 whitespace-nowrap"><Clock className="w-3 h-3" /> {fmtDur(t.durationMs)}</span>
                </div>
              </div>
              <ChevronRight className="w-3.5 h-3.5 text-muted-foreground shrink-0 mt-0.5" />
            </div>
          </button>
        ))}
        {list.length === 0 && (
          <div className="text-sm text-muted-foreground py-8 text-center">No agent tasks yet.</div>
        )}
      </div>

      {selected && (
        <div className="mt-4 border rounded-lg overflow-hidden">
          <div className="px-4 py-2.5 border-b bg-accent/5 flex items-center justify-between">
            <div>
              <div className="font-semibold text-sm">{selected.title || selected.sourceId}</div>
              <div className="text-[10px] text-muted-foreground font-mono">{selected.id}</div>
            </div>
            <StatusBadge status={selected.status} />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 p-4 text-xs">
            <div className="flex flex-col gap-1">
              <span className="text-[10px] uppercase tracking-wide text-muted-foreground">Source</span>
              <span>{selected.source} · {selected.sourceId}</span>
              <span className="text-[10px] uppercase tracking-wide text-muted-foreground mt-2">Temporal workflow</span>
              <span className="font-mono">{selected.temporalWorkflowId ?? '—'}</span>
              <span className="text-[10px] uppercase tracking-wide text-muted-foreground mt-2">Sandbox ref</span>
              <span className="font-mono">{selected.sandboxRef ?? '—'}</span>
            </div>
            <div className="flex flex-col gap-1">
              <span className="text-[10px] uppercase tracking-wide text-muted-foreground">Pod</span>
              <span className="font-mono">{selected.podName ?? '—'}</span>
              <span className="text-[10px] uppercase tracking-wide text-muted-foreground mt-2">Trace ID</span>
              <span className="font-mono">{selected.traceId ?? '—'}</span>
              <span className="text-[10px] uppercase tracking-wide text-muted-foreground mt-2">Cost / Duration</span>
              <span>${Number(selected.costUsd || '0').toFixed(4)} · {fmtDur(selected.durationMs)}</span>
            </div>
          </div>

          <div className="px-4 pb-4 text-xs">
            <div className="text-[10px] uppercase tracking-wide text-muted-foreground mb-1">Prompt</div>
            <pre className="whitespace-pre-wrap font-mono text-[11px] bg-black/30 rounded-md p-3 max-h-56 overflow-auto">{selected.prompt}</pre>

            {selected.result && (
              <>
                <div className="text-[10px] uppercase tracking-wide text-muted-foreground mb-1 mt-3">Result</div>
                <pre className="whitespace-pre-wrap font-mono text-[11px] bg-black/30 rounded-md p-3 max-h-56 overflow-auto">{selected.result}</pre>
              </>
            )}
            {selected.error && (
              <div className="mt-3 flex items-start gap-2 text-red-500">
                <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
                <span>{selected.error}</span>
              </div>
            )}
            {selected.status === 'succeeded' && (
              <div className="mt-3 flex items-center gap-2 text-emerald-500 text-xs">
                <CheckCircle2 className="w-4 h-4" /> Task succeeded
              </div>
            )}
            {selected.status === 'failed' && (
              <div className="mt-3 flex items-center gap-2 text-red-500 text-xs">
                <XCircle className="w-4 h-4" /> Task failed
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
